import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Dropdownlist } from '@/components/Dropdownlist';
import { MultiSelect } from '@/components/MultiSelect';
import { DateTimePicker } from '@/components/DateTimePicker';

import { api } from '@/services/api-class';
import { App } from '@/services/app-compat';
import moment from 'moment-timezone';
import { DATETIME_FORMAT_SERVER_UTC, notifyApp, isUWPApp, isIOSApp, isAndroidApp, isWindowsAppSdkApp } from '@/utils/window-globals';

import { b64toBlob } from '@/utils/blob';
import { saveAs } from 'file-saver';

// ============================================================================
// Types
// ============================================================================

interface ClientStartDate {
  id: number;
  name: string;
  startDate: string;
}

// ============================================================================
// AnalyticsReportScreen Component
// ============================================================================

export function AnalyticsReportScreen() {
  const { theme } = useTheme();

  // --- Initial data (loaded before mount) ---
  const [companyStartDate, setCompanyStartDate] = useState<string>('');
  const [clientStartDates, setClientStartDates] = useState<ClientStartDate[]>([]);
  const [dataLoaded, setDataLoaded] = useState(false);

  // --- Scope ---
  const [scope, setScope] = useState<string | null>(null);
  const scopeOptions = useMemo(() => [
    { code: 'analyticsCompany', value: 'Company-Wide' },
    { code: 'analyticsClient', value: 'Client(s)' },
  ], []);
  const [selectedClientIds, setSelectedClientIds] = useState<number[]>([]);

  // --- Time Period ---
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const typeOptions = useMemo(() => [
    { code: 'annual', value: 'Annual' },
    { code: 'ytd', value: 'Year to Date' },
    { code: 'monthly', value: 'Monthly' },
    { code: 'custom', value: 'Custom' },
  ], []);
  const [selectedYear, setSelectedYear] = useState<string | null>(null);
  const [selectedMonth, setSelectedMonth] = useState<string | null>(null);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);
  const [startDatePickerOpen, setStartDatePickerOpen] = useState(false);
  const [endDatePickerOpen, setEndDatePickerOpen] = useState(false);

  // --- Derived state ---
  const [startDate, setStartDate] = useState<any>(null); // moment object
  const [yearOptions, setYearOptions] = useState<Array<{ code: string; value: string }>>([]);
  const [monthOptions, setMonthOptions] = useState<Array<{ code: string; value: string }>>([]);

  // --- UI state ---
  const [isDownloading, setIsDownloading] = useState(false);

  // --- Computed ---
  const isCompanyOrClientSelected = useMemo(() => {
    if (scope === 'analyticsCompany') return true;
    if (scope === 'analyticsClient') return selectedClientIds && selectedClientIds.length > 0;
    return false;
  }, [scope, selectedClientIds]);

  const isDownloadEnabled = useMemo(() => {
    if (!scope) return false;
    if (scope === 'analyticsClient' && (!selectedClientIds || selectedClientIds.length === 0)) return false;
    if (!selectedType) return false;
    if (selectedType === 'annual') return !!selectedYear;
    if (selectedType === 'ytd' || selectedType === 'monthly') return !!selectedYear && !!selectedMonth;
    if (selectedType === 'custom') return !!customStartDate && !!customEndDate;
    return false;
  }, [scope, selectedClientIds, selectedType, selectedYear, selectedMonth, customStartDate, customEndDate]);

  // --- Helper functions ---
  const updateYearOptions = useCallback((startDateMoment: any) => {
    if (!startDateMoment) {
      setYearOptions([]);
      return;
    }

    const minYear = startDateMoment.year();
    let maxYear = moment().year();
    if (moment().month() === 0) {
      maxYear = maxYear - 1;
    }

    const years: Array<{ code: string; value: string }> = [];
    for (let year = maxYear; year >= minYear; year--) {
      years.push({ code: year.toString(), value: year.toString() });
    }
    setYearOptions(years);
  }, []);

  const updateMonthOptions = useCallback((year: string | null, startDateMoment: any) => {
    if (!year || !startDateMoment) {
      setMonthOptions([]);
      return;
    }

    const yearNum = parseInt(year);
    const minYear = startDateMoment.year();
    const currentYear = moment().year();
    const monthNames = moment.months();

    let startMonth = 1;
    let endMonth = 12;

    if (yearNum === minYear) {
      startMonth = startDateMoment.month() + 1;
    }
    if (yearNum === currentYear) {
      endMonth = moment().month();
    }

    const months: Array<{ code: string; value: string }> = [];
    for (let m = endMonth; m >= startMonth; m--) {
      months.push({ code: m.toString(), value: monthNames[m - 1] });
    }
    setMonthOptions(months);
  }, []);

  // --- Event Handlers ---
  const onScopeChange = useCallback((value: string | number | null) => {
    const newScope = value as string | null;
    setScope(newScope);

    if (newScope === 'analyticsCompany') {
      const sd = moment(companyStartDate);
      setStartDate(sd);
      updateYearOptions(sd);
    } else if (newScope === 'analyticsClient') {
      setSelectedClientIds([]);
      setStartDate(null);
      setYearOptions([]);
      setMonthOptions([]);
    }

    // Reset time selections
    setSelectedType(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setCustomStartDate(null);
    setCustomEndDate(null);
    setMonthOptions([]);
  }, [companyStartDate, updateYearOptions]);

  const onClientsChange = useCallback((newClientIds: any[]) => {
    setSelectedClientIds(newClientIds);

    if (newClientIds && newClientIds.length > 0) {
      const selectedClients = clientStartDates.filter(
        (client) => newClientIds.includes(client.id)
      );
      const momentDates = selectedClients.map(
        (client) => moment(client.startDate)
      );
      const newStartDate = moment.min(momentDates);
      setStartDate(newStartDate);
      updateYearOptions(newStartDate);
    } else {
      setStartDate(null);
      setYearOptions([]);
      setMonthOptions([]);
    }

    // Reset time selections
    setSelectedType(null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setCustomStartDate(null);
    setCustomEndDate(null);
    setMonthOptions([]);
  }, [clientStartDates, updateYearOptions]);

  const onTypeChange = useCallback((value: string | number | null) => {
    setSelectedType(value as string | null);
    setSelectedYear(null);
    setSelectedMonth(null);
    setCustomStartDate(null);
    setCustomEndDate(null);
    setStartDatePickerOpen(false);
    setEndDatePickerOpen(false);
    setMonthOptions([]);
  }, []);

  const onYearChange = useCallback((value: string | number | null) => {
    const newYear = value as string | null;
    setSelectedYear(newYear);
    setSelectedMonth(null);
    updateMonthOptions(newYear, startDate);
  }, [startDate, updateMonthOptions]);

  const onMonthChange = useCallback((value: string | number | null) => {
    setSelectedMonth(value as string | null);
  }, []);

  const openStartDatePicker = useCallback(() => {
    setEndDatePickerOpen(false);
    setTimeout(() => setStartDatePickerOpen(true), 150);
  }, []);

  const openEndDatePicker = useCallback(() => {
    setStartDatePickerOpen(false);
    setTimeout(() => setEndDatePickerOpen(true), 150);
  }, []);

  // --- Download ---
  const downloadAnalyticsReport = useCallback(() => {
    let reportStartDate: any = null;
    let reportEndDate: any = null;

    if (selectedType === 'annual') {
      reportStartDate = moment('1-1-' + selectedYear, 'M-D-YYYY');
      reportEndDate = moment('12-31-' + selectedYear, 'M-D-YYYY');
    } else if (selectedType === 'monthly' || selectedType === 'ytd') {
      const month = parseInt(selectedMonth!);

      if (selectedType === 'monthly') {
        reportStartDate = moment(month + '-1-' + selectedYear, 'M-D-YYYY');
      } else {
        reportStartDate = moment('1-1-' + selectedYear, 'M-D-YYYY');
      }

      const daysInMonth = moment(month + '-1-' + selectedYear, 'M-D-YYYY').daysInMonth();
      reportEndDate = moment(month + '-' + daysInMonth + '-' + selectedYear, 'M-D-YYYY');
    } else if (selectedType === 'custom') {
      reportStartDate = moment(customStartDate);
      reportEndDate = moment(customEndDate);
    } else {
      App.showPopupNotification('Error: Unknown analytics report type.', 'error');
      return;
    }

    const startDateUtc = reportStartDate.utc().format(DATETIME_FORMAT_SERVER_UTC);
    const endDateUtc = reportEndDate.add(23, 'hours').add(59, 'minutes').add(59, 'seconds').add(999, 'milliseconds').utc().format(DATETIME_FORMAT_SERVER_UTC);
    const timeZoneId = moment.tz.guess();

    let npsdClientIds = null;
    if (scope === 'analyticsClient') {
      npsdClientIds = selectedClientIds;
    }

    setIsDownloading(true);
    App.showPopupNotification('Preparing Analytics Report for download. Please wait.', 'success');

    const data = {
      startDateTime: startDateUtc,
      endDateTime: endDateUtc,
      timeZoneId,
      npsdClientIds,
    };

    api.analyticsReportdPdf(data, (result: any) => {
      App.showPopupNotification('Downloaded analytics report.', 'success');
      setIsDownloading(false);

      const pdfBase64String: string = result.pdfBase64;
      const filename: string = result.pdfFilename;

      if (isUWPApp || isIOSApp || isAndroidApp || isWindowsAppSdkApp) {
        notifyApp('activityReportPDFBase64', [filename, pdfBase64String]);
      } else {
        const blob = b64toBlob(pdfBase64String, 'application/pdf');
        saveAs(blob, filename);
      }
    }, () => {
      App.showPopupNotification('Error downloading analytics report.', 'error');
      setIsDownloading(false);
    });
  }, [selectedType, selectedYear, selectedMonth, customStartDate, customEndDate, scope, selectedClientIds]);

  // --- Initial Load ---
  useEffect(() => {
    api.analyticsReportdClientStartDates((result: any) => {
      const csd = result[0].startDate;
      const clients = result.slice(1);
      setCompanyStartDate(csd);
      setClientStartDates(clients);
      setDataLoaded(true);
    }, () => {
      App.showPopupNotification('Error getting client information.', 'error');
    });
  }, []);

  // --- Render ---
  if (!dataLoaded) {
    return (
      <div id="AnalyticsReportSection" className={theme}>
        <div className="kst-page-container">
          <div className="kst-loading"></div>
        </div>
      </div>
    );
  }

  return (
    <div id="AnalyticsReportSection" className={theme}>
      <div className="kst-page-container">
        <div className="kst-layout kst-layout--no-sidebar">
          <div className="kst-main-content">
            <div className="kst-page">
              <div className="kst-page-header">
                <div>
                  <h3 className="kst-page-title">Analytics Report</h3>
                  <p className="kst-page-description">Generate analytics reports for your company or specific clients.</p>
                </div>
              </div>

              {/* Report Scope Card */}
              <div className="kst-form-card">
                <div className="kst-form-card-header">Report Scope</div>
                <div className="kst-form-card-body">
                  <div className="kst-form-row">
                    <div className="kstc-form-group kst-form-col">
                      <label className="kstc-label">Scope</label>
                      <Dropdownlist
                        value={scope}
                        items={scopeOptions}
                        labelKey="value"
                        valueKey="code"
                        placeholder="Select Scope"
                        searchable={false}
                        onChange={onScopeChange}
                      />
                    </div>
                    <div className="kstc-form-group kst-form-col" style={{ display: scope === 'analyticsClient' ? undefined : 'none' }}>
                      <label className="kstc-label">Clients</label>
                      <MultiSelect
                        value={selectedClientIds}
                        items={clientStartDates}
                        labelKey="name"
                        valueKey="id"
                        placeholder="Select Clients..."
                        onChange={onClientsChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Time Period Card */}
              <div className="kst-form-card" style={{ display: isCompanyOrClientSelected ? undefined : 'none' }}>
                <div className="kst-form-card-header">Time Period</div>
                <div className="kst-form-card-body">
                  <div className="kst-form-row">
                    <div className="kstc-form-group kst-form-col">
                      <label className="kstc-label">Type</label>
                      <Dropdownlist
                        value={selectedType}
                        items={typeOptions}
                        labelKey="value"
                        valueKey="code"
                        placeholder="Select Type"
                        searchable={false}
                        onChange={onTypeChange}
                      />
                    </div>
                    <div
                      className="kstc-form-group kst-form-col"
                      style={{ display: selectedType === 'annual' || selectedType === 'ytd' || selectedType === 'monthly' ? undefined : 'none' }}
                    >
                      <label className="kstc-label">Year</label>
                      <Dropdownlist
                        value={selectedYear}
                        items={yearOptions}
                        labelKey="value"
                        valueKey="code"
                        placeholder="Select Year"
                        searchable={false}
                        sortItems={false}
                        onChange={onYearChange}
                      />
                    </div>
                    <div
                      className="kstc-form-group kst-form-col"
                      style={{ display: selectedType === 'ytd' || selectedType === 'monthly' ? undefined : 'none' }}
                    >
                      <label className="kstc-label">Month</label>
                      <Dropdownlist
                        value={selectedMonth}
                        items={monthOptions}
                        labelKey="value"
                        valueKey="code"
                        placeholder="Select Month"
                        searchable={false}
                        sortItems={false}
                        onChange={onMonthChange}
                      />
                    </div>
                  </div>

                  {/* Custom Date Range */}
                  <div className="kst-form-row" style={{ display: selectedType === 'custom' ? undefined : 'none' }}>
                    <div className="kstc-form-group kst-form-col">
                      <label className="kstc-label">Start Date</label>
                      <DateTimePicker
                        value={customStartDate}
                        type="date"
                        format="MM/DD/YYYY"
                        valueType="date"
                        clearable={true}
                        appendToBody={true}
                        popupClass={theme}
                        open={startDatePickerOpen}
                        onFocus={openStartDatePicker}
                        onChange={setCustomStartDate}
                        onOpenChange={setStartDatePickerOpen}
                      />
                    </div>
                    <div className="kstc-form-group kst-form-col">
                      <label className="kstc-label">End Date</label>
                      <DateTimePicker
                        value={customEndDate}
                        type="date"
                        format="MM/DD/YYYY"
                        valueType="date"
                        clearable={true}
                        appendToBody={true}
                        popupClass={theme}
                        open={endDatePickerOpen}
                        onFocus={openEndDatePicker}
                        onChange={setCustomEndDate}
                        onOpenChange={setEndDatePickerOpen}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Download Button */}
              <div className="kst-form-actions" style={{ display: isCompanyOrClientSelected ? undefined : 'none' }}>
                <button
                  type="button"
                  className="kstc-btn kstc-btn--primary"
                  onClick={downloadAnalyticsReport}
                  disabled={!isDownloadEnabled || isDownloading}
                >
                  {isDownloading ? (
                    <span>Preparing Report...</span>
                  ) : (
                    <span>Download PDF</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AnalyticsReportScreen;
